import { MAX_QUOTE_CHARS } from './evidencePayload';
import { normWord } from './evidenceLocator';

/**
 * Turning a transcript line, or the words selected in it, into a citation the server accepts
 * (override_evidence.py): the quote is the very text shown, so it is found in the line; at most
 * MAX_QUOTE_CHARS characters, cut at a word and marked "…"; said at the selected word's own time when the
 * transcript's word times line up with its text, else at the line's start. The search and the duplicate
 * check use the server's comparison: any case, any spacing, a trailing "…" ignored. A quote holds at least one
 * letter or digit: a lone "…" is not a quote, and the server would refuse it.
 */

const ELLIPSIS = '…';
const hasWords = (quote) => /[\p{L}\p{N}]/u.test(String(quote ?? '').replace(/…$/, ''));

export const squash = (text) => String(text ?? '').replace(/\s+/g, ' ').trim();

/** At most `max` UTF-16 units, the "…" included (the server counts code points, never more than this). */
export function capQuote(text, max = MAX_QUOTE_CHARS) {
  const clean = squash(text);
  if (clean.length <= max) return clean;
  let cut = max - 1;
  if (cut > 0 && /[\uD800-\uDBFF]/.test(clean[cut - 1])) cut -= 1;   // never split a surrogate pair
  const space = clean.lastIndexOf(' ', cut);
  const end = space >= Math.floor(max * 0.6) ? space : cut;
  return `${clean.slice(0, end).trimEnd()}${ELLIPSIS}`;
}

/** The selection [from, to) in `text` widened to whole words, or null when it holds no word. */
export function widenToWords(text, from, to) {
  const s = String(text ?? '');
  let a = Math.max(0, Math.min(from, to));
  let b = Math.min(s.length, Math.max(from, to));
  while (a < b && /\s/.test(s[a])) a += 1;
  while (b > a && /\s/.test(s[b - 1])) b -= 1;
  if (a >= b) return null;
  while (a > 0 && !/\s/.test(s[a - 1])) a -= 1;
  while (b < s.length && !/\s/.test(s[b])) b += 1;
  return { from: a, to: b };
}


/** When the word at `wordIndex` (counting DisplayText's words) was said: WordConfidence's own time when its
 * words line up one to one with DisplayText's, kept inside the line; else the line's start. */
export function wordStartOf(segment, wordIndex) {
  const start = Number(segment?.SegmentStartTime) || 0;
  const end = Number(segment?.SegmentEndTime);
  const shown = String(segment?.DisplayText ?? '').split(/\s+/).filter(Boolean);
  const timed = (Array.isArray(segment?.WordConfidence) ? segment.WordConfidence : []).filter((w) => w && w.Text);
  if (!(wordIndex >= 0 && wordIndex < shown.length) || timed.length !== shown.length) return start;
  if (!timed.every((w, i) => normWord(w.Text) === normWord(shown[i]))) return start;
  const t = Number(timed[wordIndex].StartTime);
  if (!Number.isFinite(t)) return start;
  const last = Number.isFinite(end) && end >= start ? end : start;
  return Math.min(Math.max(t, start), last);
}

/**
 * A citation of `segment` (line number `seg`): the whole line (its own text — the original on a translated
 * line), or the words selected between `from` and `to` in `field` ('DisplayText' | 'TranslatedText').
 * {quote, start, seg, source: 'human'}, or null when there are no words.
 */
export function citeFromLine(segment, seg, { field = 'DisplayText', from = null, to = null } = {}) {
  const lineStart = Number(segment?.SegmentStartTime) || 0;
  if (from === null || to === null) {
    const quote = capQuote(segment?.DisplayText);
    return hasWords(quote) ? { quote, start: lineStart, seg, source: 'human' } : null;
  }
  const text = String(segment?.[field] ?? '');
  const span = widenToWords(text, from, to);
  if (!span) return null;
  const quote = capQuote(text.slice(span.from, span.to));
  if (!hasWords(quote)) return null;
  const start = field === 'DisplayText'
    ? wordStartOf(segment, text.slice(0, span.from).split(/\s+/).filter(Boolean).length)
    : lineStart;
  return { quote, start, seg, source: 'human' };
}

/** The lines a search finds — by text, English or speaker, any case: [{seg, segment, speaker}]. */
export function matchLines(segments, query, speakerOf) {
  const rows = (Array.isArray(segments) ? segments : []).map((segment, seg) => ({
    seg, segment, speaker: speakerOf ? speakerOf(segment, seg) : String(segment?.SegmentSpeaker ?? ''),
  }));
  const q = squash(query).toLowerCase();
  if (!q) return rows;
  return rows.filter(({ segment, speaker }) => [segment?.DisplayText, segment?.TranslatedText, speaker]
    .some((text) => squash(text).toLowerCase().includes(q)));
}

/** `text` around each case-insensitive match of `query`, for <mark>: [{text, hit}]. */
export function markParts(text, query) {
  const s = String(text ?? '');
  const q = String(query ?? '').trim();
  if (!q) return [{ text: s, hit: false }];
  const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push({ text: s.slice(last, m.index), hit: false });
    parts.push({ text: m[0], hit: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push({ text: s.slice(last), hit: false });
  return parts.length ? parts : [{ text: s, hit: false }];
}

const lowered = (text) => squash(text).toLowerCase();

/**
 * What makes two quotes on a question the same, as the server decides it (override_evidence.checked_evidence): a
 * kept AI quote by its words; a citation by its line and its words, a trailing "…" aside, wherever in the line it
 * was said. A citation that names no line (nothing is saved without one) falls back to its time.
 */
export const evidenceKey = (row) => {
  if (row?.source !== 'human') return `ai:${lowered(row?.quote)}`;
  const body = lowered(String(row.quote ?? '').trim().replace(/…$/, ''));
  return Number.isInteger(row.seg) ? `line:${row.seg}:${body}` : `at:${Number(row.start).toFixed(3)}:${body}`;
};
